import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import EmailOutlined from '@mui/icons-material/EmailOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

import { useTranslation } from 'react-i18next';

import PortalLayout from '../../../../../../layout/Portal';
import ROUTES from '../../../../../../core/constants/routes.constant';
import { STUDY_EMAIL, STUDY_TEL } from '../../../../../../core/constants/content.constant';

import { useAppDispatch } from '../../../../../../store';
import { withdrawalReset } from '../../../../../../store/participant';
import { chartColours } from '../../../../../../core/constants/colours.constants';
import PageContainer from '../../../../../../components/content/PageContainer';

function WithdrawalIntroScene() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('portal');

  useEffect(() => {
    dispatch(withdrawalReset());
  }, [dispatch]);

  return (
    <PortalLayout>
      <PageContainer
        title={t('withdrawal.title')}
        icon={
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${chartColours[5]}26`,
              color: chartColours[5],
              flexShrink: 0,
              border: 1,
              borderColor: `${chartColours[5]}55`,
            }}
          >
            <WarningAmberOutlined sx={{ fontSize: 34 }} />
          </Box>
        }
      >
        <Box
          sx={{
            '& p': {
              lineHeight: 1.65,
            },
          }}
        >
          <Typography variant="body1" component="p" paragraph>
            {t('withdrawal.para1')}
          </Typography>

          <Typography variant="body1" component="p" paragraph>
            {t('withdrawal.para2')}
          </Typography>

          <Typography variant="body1" component="p" paragraph>
            {t('withdrawal.para3')}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mt: 3,
            mb: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'grey.50',
          }}
        >
          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              display: 'grid',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${chartColours[1]}26`,
                  color: chartColours[1],
                  border: 1,
                  borderColor: `${chartColours[1]}55`,
                  flexShrink: 0,
                }}
              >
                <EmailOutlined sx={{ fontSize: 24 }} />
              </Box>

              <Typography variant="body1" component="p" sx={{ fontWeight: 700 }}>
                {t('withdrawal.labels.email')}: {STUDY_EMAIL}
              </Typography>
            </Box>

            <Typography
              variant="body2"
              component="p"
              color="text.secondary"
              sx={{
                fontWeight: 800,
                pl: { xs: 0, sm: 7.25 },
              }}
            >
              {t('withdrawal.labels.or')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${chartColours[2]}26`,
                  color: chartColours[2],
                  border: 1,
                  borderColor: `${chartColours[2]}55`,
                  flexShrink: 0,
                }}
              >
                <PhoneOutlined sx={{ fontSize: 24 }} />
              </Box>

              <Typography variant="body1" component="p" sx={{ fontWeight: 700 }}>
                {t('withdrawal.labels.telephone')}: {STUDY_TEL}.
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1,
          }}
        >
          <Button
            variant="contained"
            color="error"
            size="large"
            component={RouterLink}
            data-testid="withdrawal1-btn"
            to={ROUTES.portalWithdrawalOptions}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 360 },
              px: 3,
              py: 1.4,
              fontWeight: 900,
              textTransform: 'none',
              boxShadow: 3,
            }}
          >
            {t('withdrawal.button')}
          </Button>
        </Box>
      </PageContainer>
    </PortalLayout>
  );
}

export default WithdrawalIntroScene;
